import React, { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa6";

const VIDEO_URL_RE = /\.(mp4|webm|mov|m4v|ogv|ogg)(\?|#|$)/i;

export function isVideoMediaUrl(url) {
  return VIDEO_URL_RE.test(String(url || "").trim());
}

function dedupeUrls(urls) {
  const seen = new Set();
  const out = [];
  for (const raw of urls) {
    const url = String(raw || "").trim();
    if (!url || seen.has(url)) continue;
    seen.add(url);
    out.push(url);
  }
  return out;
}

function MediaSlide({ url }) {
  if (isVideoMediaUrl(url)) {
    return (
      <video
        src={url}
        controls
        playsInline
        preload="metadata"
        className="max-h-full max-w-full object-contain"
      />
    );
  }
  return <img src={url} alt="" className="max-h-full max-w-full object-contain" />;
}

/**
 * Touch-friendly media carousel (Embla). Safe to reuse on web and Capacitor clients.
 */
const MediaCarousel = ({ urls = [], className = "" }) => {
  const slides = dedupeUrls(urls);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: "center" });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return undefined;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.reInit();
  }, [emblaApi, slides.length]);

  if (!slides.length) {
    return null;
  }

  if (slides.length === 1) {
    return (
      <div className={`flex h-full w-full items-center justify-center ${className}`.trim()}>
        <MediaSlide url={slides[0]} />
      </div>
    );
  }

  return (
    <div className={`relative h-full w-full ${className}`.trim()}>
      <div className="h-full overflow-hidden" ref={emblaRef}>
        <div className="flex h-full touch-pan-y">
          {slides.map((url) => (
            <div
              key={url}
              className="flex min-w-0 flex-[0_0_100%] items-center justify-center px-1"
            >
              <MediaSlide url={url} />
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        aria-label="Previous image"
        disabled={!canScrollPrev}
        onClick={() => emblaApi?.scrollPrev()}
        className="absolute left-1 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white/95 text-slate-700 shadow-sm transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-40"
      >
        <FaChevronLeft className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        aria-label="Next image"
        disabled={!canScrollNext}
        onClick={() => emblaApi?.scrollNext()}
        className="absolute right-1 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white/95 text-slate-700 shadow-sm transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-40"
      >
        <FaChevronRight className="h-3.5 w-3.5" />
      </button>

      <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5 px-2">
        {slides.map((url, index) => (
          <button
            key={`${url}-${index}`}
            type="button"
            aria-label={`Go to slide ${index + 1}`}
            onClick={() => emblaApi?.scrollTo(index)}
            className={`h-1.5 rounded-full transition-all ${
              index === selectedIndex ? "w-4 bg-indigo-600" : "w-1.5 bg-slate-300"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export function buildProductMediaUrls(primaryUrl, additionalImages) {
  const additional = Array.isArray(additionalImages)
    ? additionalImages.map((url) => String(url || "").trim()).filter(Boolean)
    : [];
  return dedupeUrls([primaryUrl, ...additional]);
}

export default MediaCarousel;
